import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { fetchReportRecords } from '../services/reportApi';
import { formatCount, formatDateTime, formatRequestId, formatStatusLabel, formatTimeValue } from '../utils/format';

export default function ReportDetailView({ report, onBack }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const per = 50;

  useEffect(() => {
    if (!report) return;

    let mounted = true;

    async function loadRecords() {
      setLoading(true);
      try {
        const payload = await fetchReportRecords(report.id, page, per);
        if (mounted) {
          setRecords(payload.data || []);
        }
      } catch (error) {
        if (mounted) {
          toast.error(error.message || 'Failed to load report records.');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    void loadRecords();

    return () => {
      mounted = false;
    };
  }, [report, page]);

  if (!report) return null;

  const totalPages = Math.ceil((report.count || 0) / per);

  return (
    <div className="panel report-detail-view" style={{ width: '100%' }}>
      <div className="panel__header panel__header--split">
        <div>
          <span className="panel__eyebrow">Report Details</span>
          <h2>{report.filename}</h2>
          <p>Request ID: {formatRequestId(report.id)}</p>
        </div>
        <div>
          <button className="button button--secondary" onClick={onBack} type="button">
            &larr; Back to Reports
          </button>
        </div>
      </div>

      <div className="summary-strip" style={{ marginBottom: '24px' }}>
        <div>
          <span>Total Rows</span>
          <strong>{formatCount(report.count)}</strong>
        </div>
        <div>
          <span>Created</span>
          <strong>{formatDateTime(report.createdAt)}</strong>
        </div>
        <div>
          <span>Rate</span>
          <strong>{report.rate || 0} msg/sec</strong>
        </div>
      </div>

      <div className="table-shell" style={{ overflowX: 'auto' }}>
        {loading ? (
          <div className="empty-state">Loading records...</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>#</th>
                <th>Receiver</th>
                <th>Message</th>
                <th>Content</th>
                <th>Status</th>
                <th>Sent Time</th>
                <th>Delivered Time</th>
                <th>Read Time</th>
              </tr>
            </thead>
            <tbody>
              {records.length === 0 ? (
                <tr>
                  <td className="table-empty" colSpan={9}>
                    No records found for this report.
                  </td>
                </tr>
              ) : (
                records.map((row) => (
                  <tr key={row.index}>
                    <td>{row.id}</td>
                    <td>{row.index}</td>
                    <td>{row.receiver}</td>
                    <td style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.message}>{row.message}</td>
                    <td style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.content}>{row.content}</td>
                    <td>
                      <span className={`status-pill status-pill--${row.status}`}>{formatStatusLabel(row.status)}</span>
                    </td>
                    <td>{formatTimeValue(row.sentTime)}</td>
                    <td>{formatTimeValue(row.deliveredTime)}</td>
                    <td>{formatTimeValue(row.readTime)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {!loading && totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px' }}>
          <span className="muted-text" style={{ fontSize: '0.9rem' }}>Showing page {page} of {totalPages}</span>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="button button--secondary" disabled={page === 1} onClick={() => setPage(page - 1)} style={{ minHeight: '36px', padding: '0 14px' }}>Previous</button>
            <button className="button button--secondary" disabled={page >= totalPages} onClick={() => setPage(page + 1)} style={{ minHeight: '36px', padding: '0 14px' }}>Next</button>
          </div>
        </div>
      )}
    </div>
  );
}
